/**
 * Username Generator - Creates unique, fun usernames for users
 * Combines adjectives + nouns + optional numbers for variety
 */

import { containsProfanity } from './contentFilter';

const ADJECTIVES = [
  'Cool', 'Happy', 'Sunny', 'Bright', 'Swift', 'Bold', 'Chill', 'Wild',
  'Epic', 'Cosmic', 'Urban', 'Zen', 'Brave', 'Clever', 'Crafty', 'Eager',
  'Fancy', 'Gentle', 'Jolly', 'Keen', 'Lively', 'Merry', 'Noble', 'Proud',
  'Quick', 'Witty', 'Zesty', 'Stellar', 'Smooth', 'Fresh', 'Funky', 'Groovy',
  'Lucky', 'Mighty', 'Nifty', 'Peppy', 'Quirky', 'Rapid', 'Savvy', 'Snappy',
  'Spry', 'Super', 'Vital', 'Zippy', 'Active', 'Daring', 'Electric', 'Golden',
  'Hyper', 'Jazzy', 'Mystic', 'Nimble', 'Perky', 'Radiant', 'Spiffy', 'Sporty',
  'Stellar', 'Vibrant', 'Wicked', 'Awesome', 'Dapper', 'Dynamic', 'Fierce',
  'Cosmic', 'Lunar', 'Solar', 'Ocean', 'Forest', 'River', 'Storm', 'Thunder'
];

const NOUNS = [
  'Vibe', 'Wave', 'Fox', 'Wolf', 'Bear', 'Eagle', 'Tiger', 'Lion',
  'Panda', 'Ninja', 'Pilot', 'Rider', 'Racer', 'Star', 'Moon', 'Sun',
  'Phoenix', 'Dragon', 'Hawk', 'Falcon', 'Raven', 'Panther', 'Cheetah',
  'Dolphin', 'Shark', 'Whale', 'Otter', 'Badger', 'Lynx', 'Cobra', 'Python',
  'Falcon', 'Sparrow', 'Heron', 'Falcon', 'Voyager', 'Explorer', 'Seeker',
  'Wanderer', 'Nomad', 'Ranger', 'Scout', 'Hunter', 'Warrior', 'Knight',
  'Sage', 'Wizard', 'Mystic', 'Oracle', 'Prophet', 'Hero', 'Champion',
  'Legend', 'Master', 'Expert', 'Ace', 'Pro', 'Captain', 'Chief', 'Boss',
  'Titan', 'Giant', 'Maverick', 'Rebel', 'Rogue', 'Spirit', 'Soul', 'Mind',
  'Heart', 'Dreamer', 'Thinker', 'Maker', 'Builder', 'Creator', 'Artist'
];

/**
 * Generate a random username
 * Format: [Adjective][Noun] or [Adjective][Noun][Number]
 */
export function generateUsername(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];

  // 30% chance to add a number (2-4 digits) for extra uniqueness
  const addNumber = Math.random() < 0.3;

  if (addNumber) {
    const number = Math.floor(Math.random() * 9999) + 1;
    return `${adjective}${noun}${number}`;
  }

  return `${adjective}${noun}`;
}

/**
 * Generate multiple username options
 */
export function generateUsernameOptions(count: number = 3): string[] {
  const options = new Set<string>();

  // Generate until we have the requested count of unique options
  while (options.size < count) {
    options.add(generateUsername());
  }

  return Array.from(options);
}

/**
 * Check if a username matches the valid format
 */
export function isValidUsernameFormat(username: string): boolean {
  // Must be 3-20 characters, alphanumeric and underscores only
  return /^[a-zA-Z0-9_]{3,20}$/.test(username);
}

/**
 * Generate a clean username that passes profanity filters
 * Retries up to maxRetries times to find a clean username
 *
 * @param maxRetries Maximum number of generation attempts (default: 10)
 * @returns A clean username, or null if all attempts fail
 */
export function generateCleanUsername(maxRetries: number = 10): string | null {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const username = generateUsername();

    // Check if the username contains profanity
    if (!containsProfanity(username)) {
      return username;
    }

    // If profane, continue to next attempt
    console.log(`Username generation attempt ${attempt + 1}: rejected due to profanity`);
  }

  // All attempts failed to generate a clean username
  console.warn(`Failed to generate clean username after ${maxRetries} attempts`);
  return null;
}
